<script setup>
import {ref, onMounted, watch, computed, getCurrentInstance, defineProps } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageLoadingStore } from "../../../../../stores/pageLoading";
import adminJobfairStateService from "../../../services/jobfair/adminJobfairStateService";
import mypageJobFairService from "../../../../user/services/mypage/jobFair/mypageJobFairService";
import JobFairJobNoticeDetailModal from "../../../../user/components/modal/jobFair/JobFairJobNoticeDetailModal.vue";
import JobFairResumeDetailModal from "../../../../user/components/modal/jobFair/JobFairResumeDetailModal.vue";

const route = useRoute();
const router = useRouter()
const loadingStore = usePageLoadingStore();
const { $gbNameKR, $toast, $number, $pageListBoardNo, $yyyymmddHHmmss, alert, confirm } = getCurrentInstance().appContext.config.globalProperties;
const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(0);
const totalCount = ref(0);
const list = ref([]);

const jobNoticeModal = ref(null);
const resumeModal = ref(null);

const props = defineProps({
  userId: {
    type: [String, Number],
    required: true,
  }
});

const pageSizeGroup = ref([
  { label:'10개씩 보기', value: 10 },
  { label:'30개씩 보기', value: 30 },
  { label:'50개씩 보기', value: 50 },
  { label:'100개씩 보기', value: 100 },
]);

// 페이지 선택
const onSelectPage = (pageNo) => {
  currentPage.value = pageNo;
  retrieveList();
}

// 검색조건 - 페이지 사이즈 변경. 10,30,50,100
const onSelectPageSize = (option) => {
  pageSize.value = option.value;
  currentPage.value = 1;
  retrieveList();
}

const retrieveList = () => {
  const paramObj = {
    pageNumber: currentPage.value,
    size: pageSize.value,
    userId: props.userId,
  };

  loadingStore.loading = true;
  adminJobfairStateService.retrieveReceivedInterviewProposalEntryListPagingByUserId(paramObj)
    .then( async (res) => {
      loadingStore.loading = false;
      if (res.data.code.toLowerCase() === "success") {
        const result = res.data.result;
        totalPages.value = result.totalPages;
        totalCount.value = result.totalElements;
        list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
      }
    })
    .catch((res) => {
      loadingStore.loading = false;
      $toast("error", res);
    });
}

// 채용공고 보기
const onClickJobNotice = async (jobNotice) => {
  await jobNoticeModal.value.show({
    jobNoticeSeq: jobNotice.jobNoticeSeq,
    corpId: jobNotice.corpId,
  });
}

// 이력서 보기
const onClickResume = async (resumeId, delYn) => {
  if (delYn === 'Y') {
    alert(["삭제된 이력서 입니다."]);
  } else {
    await resumeModal.value.show({
      resumeId,
      hideFooterBtn: true,
    });
  }
}

// 받은 면접제안 상태변경
const onClickIntvPropState = async (row, stateGb) => {
  let modalContent, okButtonText;
  if (stateGb === '02') {
    modalContent = ["면접 제안을 수락하시겠습니까?", "'제안수락'으로 상태가 변경됩니다."];
    okButtonText = "제안 수락";
  } else {
    modalContent = ["면접 제안을 거절하시겠습니까?", "'제안거절'로 상태가 변경됩니다."];
    okButtonText = "제안 거절";
  }
  const ok = await confirm(modalContent, okButtonText);
  if(ok) {
    const paramObj = {
      jobNoticeSeq: row.jobNoticeSeq,
      resumeId: row.resumeId,
      intvPropStateGb: stateGb
    };

    mypageJobFairService.updateIntvPropStateGb(paramObj)
      .then( (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          //const result = res.data.result;
          alert('변경하였습니다.');
          retrieveList();
        } else {
          alert('변경을 실패하였습니다.');
        }
      })
      .catch( (res) =>  {
        loadingStore.loading = false;
        alert('변경을 실패하였습니다.');
      });
  }
}

const openDetailInNewTab = (linkName, id, query = {}) => {
  const url = router.resolve({
    name: linkName,
    params: { id },
    query,
  }).href;

  window.open(url, '_blank');
}

onMounted(() => {
  retrieveList();
})
</script>

<template>
  <div>
    <!-- s: 버튼영역 -->
    <div class="list-top">
      <p>전체 <span>{{ $number(totalCount)}}</span>개</p>
      <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
    </div>
    <!-- e: 버튼영역 -->
    <div class="table-form">
      <table class="horizontal" v-if="list !== null">
        <tr>
          <th>NO</th>
          <th>기업명</th>
          <th>채용공고 제목</th>
          <th>마스터</th>
          <th>관심 카테고리</th>
          <th>면접 방식</th>
          <th>내 이력서 제목</th>
          <th>제안 받은일</th>
          <th>상태</th>
          <th>상태변경</th>
        </tr>
        <tr v-for="member in list">
          <td>{{ member.boardNo }}</td>
          <td class="clickable blue text-overflow long-overflow">
            <span @click="openDetailInNewTab('CorporationDetail', member.corpId, { tab: 3, tab2: 'job-postings' })">{{ member.corpName }}</span>
          </td>
          <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(member)">{{ member.jobName }}</td>
          <td class="clickable blue">
            <span @click="openDetailInNewTab('MemberDetail', member.userId)">{{ member.userName }}</span>
          </td>
          <td>{{ member.interests }}</td>
          <td>{{ $gbNameKR("INTV_GB", member.intvGb) }}</td>
          <td class="clickable blue text-overflow long-overflow" @click="onClickResume(member.resumeId, member.delYn)">{{ member.title }}</td>
          <td>{{ $yyyymmddHHmmss(member.createDate) }}</td>
          <td :class="member.intvPropStateGb === '02' || member.intvPropStateGb === '05' ? 'blue' : member.intvPropStateGb === '03' || member.intvPropStateGb === '06' ? 'red' : member.intvPropStateGb === '04' ? 'gray' : ''">
            {{ $gbNameKR("USER_INTV_PROP_STATE_GB", member.intvPropStateGb) }}
          </td>
          <td>
            <div v-if="member.intvPropStateGb === '01'">
              <KSATextButton text="제안수락" @click="onClickIntvPropState(member, '02')" class="clickable black"></KSATextButton>&nbsp;
              <KSATextButton text="제안거절" @click="onClickIntvPropState(member, '03')" class="clickable black"></KSATextButton>
            </div>
            <div v-else-if="member.intvPropStateGb === '02'">
              <KSATextButton text="제안거절" @click="onClickIntvPropState(member, '03')" class="clickable black"></KSATextButton>
            </div>
            <div v-else-if="member.intvPropStateGb === '03' && member.delYn !== 'Y'">
              <KSATextButton text="제안수락" @click="onClickIntvPropState(member, '02')" class="clickable black"></KSATextButton>&nbsp;
            </div>
            <div v-else>-</div>
          </td>
        </tr>
      </table>
      <div class="result-none" v-if="list.length === 0">
        받은 면접제안이 없습니다.
      </div>
    </div>
    <Paginate
      v-if="list.length > 0"
      v-model="currentPage"
      :page-count="totalPages"
      :click-handler="onSelectPage"
    />
  </div>
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
  <JobFairResumeDetailModal ref="resumeModal" /> <!-- 이력서 보기-->
</template>

<style scoped>
</style>
